/**
 * StoqWaitlistAdapter — narrow provider integration for STOQ's Create
 * Intent API. This is the ONLY file in the theme allowed to know STOQ's
 * endpoint, payload shape, or response format. No picker or interaction
 * state code should reference `window._RestockRocket`, `intents.json`,
 * or any STOQ-specific field name directly.
 *
 * Dual export like vaultify-waitlist-core.js: the pure functions
 * (payload building, market ID validation, response classification) are
 * requireable from `node:test` with zero DOM/network dependency. The
 * network-touching functions accept an injectable `fetchImpl` for the
 * same reason — real network calls are never required to test them.
 *
 * Provider registration (the browser-only part, guarded below) makes
 * this adapter discoverable by the interaction state via
 * VaultifyPickers.registerWaitlistProvider() — see vaultify-pickers.js.
 * The interaction state calls provider.isReady()/.submit() without ever
 * importing this file directly, so a future alternate provider could be
 * swapped in without touching picker or interaction-state code.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.StoqWaitlistAdapter = factory();
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  const INTENTS_ENDPOINT = 'https://app.stoqapp.com/api/v1/intents.json';

  // ── Market ID ────────────────────────────────────────────────────────────

  /**
   * Validates a raw market ID value. Accepts only a clean unsigned
   * integer (as a number or numeric string). Rejects null, undefined,
   * empty string, non-numeric text, and Shopify GID strings
   * (e.g. "gid://shopify/Market/12345").
   *
   * @returns {number|null}
   */
  function normaliseMarketId(raw) {
    if (raw === null || raw === undefined) return null;
    const str = String(raw).trim();
    if (!/^\d+$/.test(str)) return null;
    return Number(str);
  }

  /**
   * Resolves the current Shopify market ID from STOQ's JS API, if
   * present. Supports either a synchronous or Promise-returning
   * `marketId()` method — the actual runtime type has not been
   * independently confirmed, so both are handled rather than assumed.
   * Never throws; returns null on any failure so callers can safely
   * omit `shopify_market_id` rather than block submission.
   *
   * @param {{ _RestockRocket?: { marketId?: () => unknown } }} globalObj
   */
  async function resolveMarketId(globalObj) {
    try {
      const api = globalObj && globalObj._RestockRocket;
      if (!api || typeof api.marketId !== 'function') return null;
      let raw = api.marketId();
      if (raw && typeof raw.then === 'function') raw = await raw;
      return normaliseMarketId(raw);
    } catch (_err) {
      return null;
    }
  }

  // ── Payload construction ────────────────────────────────────────────────

  /**
   * Builds one Create Intent payload for one resolved variant.
   *
   * @param {object} params
   * @param {object} params.variant - trusted resolved variant (id, title, sku, ...).
   * @param {object} params.product - { id, title, vendor }.
   * @param {number|null} [params.marketId] - validated market ID, or null to omit.
   * @param {object|null} params.customer - { shopify_customer_id?, name?, email, accepts_marketing?, locale? } or null for guest.
   */
  function buildIntentPayload({ variant, product, marketId, customer }) {
    if (!variant || variant.id === undefined || variant.id === null) {
      throw new TypeError('buildIntentPayload requires a resolved variant with an id');
    }
    if (!product || product.id === undefined || product.id === null) {
      throw new TypeError('buildIntentPayload requires a product with an id');
    }
    if (!customer || !customer.email) {
      throw new TypeError('buildIntentPayload requires a customer with an email');
    }

    const intent = {
      shopify_variant_id: variant.id,
      shopify_product_id: product.id,
      channel: 'email',
      quantity: 1,
      source: 'api',
    };
    if (marketId !== null && marketId !== undefined) {
      intent.shopify_market_id = marketId;
    }

    const customerPayload = {
      email: customer.email,
      accepts_marketing: false, // waitlist signup is never marketing consent
    };
    if (customer.shopify_customer_id) {
      customerPayload.shopify_customer_id = String(customer.shopify_customer_id);
    }
    if (customer.name) customerPayload.name = customer.name;
    if (customer.locale) customerPayload.locale = customer.locale;

    const productPayload = {
      title: product.title,
      variant_title: variant.title,
    };
    if (product.vendor) productPayload.vendor = product.vendor;
    if (variant.sku) productPayload.sku = variant.sku;
    if (typeof product.variant_count === 'number') productPayload.variant_count = product.variant_count;

    return { intent, customer: customerPayload, product: productPayload };
  }

  // ── Response classification ─────────────────────────────────────────────

  /**
   * Classifies a Create Intent response. A successful fetch is not
   * automatically a successful signup — both HTTP status and any
   * body-level error structure are treated as significant. STOQ's exact
   * success/duplicate/error schema has not been independently verified
   * (public docs did not expose it); this classification is
   * intentionally conservative and documented as provisional pending
   * live-response validation (see architecture doc).
   *
   * @param {number} status - HTTP status code.
   * @param {unknown} body - parsed JSON response body, or null if unparseable.
   */
  function classifyResponse(status, body) {
    const bodyErrors = body && typeof body === 'object' && Array.isArray(body.errors) ? body.errors : [];

    if (status < 200 || status >= 300) {
      return { success: false, reason: 'http-error', errors: bodyErrors };
    }
    if (bodyErrors.length > 0) {
      return { success: false, reason: 'provider-error', errors: bodyErrors };
    }
    return { success: true, reason: null, errors: [] };
  }

  // ── Network ──────────────────────────────────────────────────────────────

  /**
   * Creates one intent. `fetchImpl` is injectable so this is testable
   * without a real network call (Node's global fetch works fine as the
   * default in both the browser and Node 18+, but tests can pass a mock).
   *
   * @returns {Promise<{success: boolean, reason: string|null, errors: Array, status: number|null}>}
   */
  async function createIntent(payload, { shopDomain, fetchImpl = fetch } = {}) {
    if (!shopDomain) throw new TypeError('createIntent requires shopDomain');
    try {
      const response = await fetchImpl(INTENTS_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Shop-Domain': shopDomain,
        },
        body: JSON.stringify(payload),
      });
      let body = null;
      try {
        body = await response.json();
      } catch (_parseErr) {
        body = null;
      }
      const classification = classifyResponse(response.status, body);
      return { ...classification, status: response.status };
    } catch (_networkErr) {
      return { success: false, reason: 'network-error', errors: [], status: null };
    }
  }

  /**
   * Submits one intent per queued variant, sequentially. Returns a
   * provider-neutral result — picker/interaction-state code never sees
   * raw STOQ response shapes.
   *
   * @param {string[]} variantIds - exact Shopify variant IDs (as strings).
   * @param {{ variants: object[], product: object, shopDomain: string, marketId: number|null, customer: object }} context
   * @param {{ fetchImpl?: Function }} [deps]
   */
  async function submitWaitlistQueue(variantIds, context, deps = {}) {
    const { variants, product, shopDomain, marketId, customer } = context;
    const succeeded = [];
    const failed = [];

    for (const variantId of variantIds) {
      const variant = variants.find((v) => String(v.id) === String(variantId));
      if (!variant) {
        failed.push({ variantId, reason: 'unknown-variant', providerErrors: [] });
        continue;
      }

      let payload;
      try {
        payload = buildIntentPayload({ variant, product, marketId, customer });
      } catch (err) {
        failed.push({ variantId, reason: 'invalid-payload', providerErrors: [String(err.message)] });
        continue;
      }

      const result = await createIntent(payload, { shopDomain, fetchImpl: deps.fetchImpl });
      if (result.success) {
        succeeded.push({ variantId, providerResult: { status: result.status } });
      } else {
        failed.push({ variantId, reason: result.reason, providerErrors: result.errors });
      }
    }

    return { succeeded, failed };
  }

  const api = {
    INTENTS_ENDPOINT,
    normaliseMarketId,
    resolveMarketId,
    buildIntentPayload,
    classifyResponse,
    createIntent,
    submitWaitlistQueue,
  };

  // ── Provider registration (browser-only) ────────────────────────────────
  // Registers this adapter with VaultifyPickers so the interaction state
  // can query waitlist capability/submit without importing STOQ specifics
  // directly. Guarded so this file stays require()-able under Node for
  // testing the pure functions above with zero side effects.
  if (typeof window !== 'undefined' && window.VaultifyPickers && window.VaultifyPickers.registerWaitlistProvider) {
    window.VaultifyPickers.registerWaitlistProvider({
      name: 'stoq',
      isReady() {
        // This adapter's submit() posts directly to STOQ's Create Intent
        // API via fetch() — it never calls into window._RestockRocket or
        // any part of STOQ's own storefront SDK (the only optional use of
        // that global is resolveMarketId(), which already degrades to
        // omitting shopify_market_id if it's unavailable). The only real
        // requirements are fetch() itself and the scoped product-interaction
        // context (shop domain, variants, customer), which
        // ProductInteractionState reads independently server-side — see
        // submitWaitlist() in vaultify-pickers.js. Checking for
        // window._RestockRocket.openInlineForm here was wrong: that method
        // belongs to STOQ's own inline-form UI, which this adapter never
        // uses, and isn't guaranteed to be loaded outside a full
        // product-page context — confirmed in the field that Restock
        // Rocket's reduced "collection" bundle (loaded for Quick Add) omits
        // it entirely, which made the whole custom Create Intent flow look
        // broken in Quick Add when it was never actually blocked.
        return typeof fetch === 'function';
      },
      async submit(variantIds, context) {
        const marketId = await resolveMarketId(window);
        return submitWaitlistQueue(variantIds, { ...context, marketId });
      },
    });
  }

  return api;
});
